import { Injectable } from '@angular/core';
import { environment } from './../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { catchError } from 'rxjs/operators';


@Injectable({
    providedIn: 'root'
})
export class WalletService {

    constructor(private http: HttpClient) { }

    // Get Wallet details (balance, currency, etc.)
    getWalletDetails(agencyId: string): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}wallet/details/${agencyId}`);
    }
    /** 
     * Create a new payment method 
     * */
    createPaymentMethod(paymentMethod: any): Observable<object> {
        return this.http.post(`${environment.apiUrl}payment_methods/create`, paymentMethod);
    }
    /** 
     * Add payment methods using stripe session id 
     * */
    addPaymentMethodFromStripeSession(sessionId: any): Observable<object> {
        return this.http.post(`${environment.apiUrl}payment_methods/add`, sessionId);
    }
    /** 
     * Delete payment method 
     * */
    deletePaymentMethod(id: string): Observable<object> {
        return this.http.delete(`${environment.apiUrl}payment_methods/delete/${id}`);
    }
    /** 
     * Get user transactions
     * */
    getTransactions(payload: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}wallet-transactions`, payload);
    }
    /**
     * Our own invoice of a transaction ({ invoice_id }), never Stripe's
     * */
    getTransactionInvoice(transactionId: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}wallet-transactions/invoice/${transactionId}`, {});
    }
    /** 
     * Get agency subscription
     * */
    getAgencySubscription(agencyId: string): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}wallet/${agencyId}/subscription`);
    }
    /** 
     * Get All Payment Methods 
     * */
    getAllPaymentMethods(payload: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}payment_methods`, payload);
    }
    /**
     * Create SetupIntent
    */
    createSetupIntent(userId: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}wallet/setup-intent`, userId);
    }
    /**
     * Start Stripe Checkout
    */
    startCheckoutForProduct(payload: any) {
        return this.http.post<any>(`${environment.apiUrl}wallet/product-checkout-session`, payload);
    }
    /**
     * Convert EUR to USD using a free API
     * @param euroAmount Amount in EUR
     * @returns Observable<number> amount in USD
     */
    convertEuroToUsd(euroAmount: number): Observable<number> {
        const url = 'https://api.exchangerate.host/latest?base=EUR&symbols=USD';

        return this.http.get<any>(url).pipe(
            map((res: any) => {
                if (res && res.rates && res.rates.USD) {
                    return euroAmount * res.rates.USD;
                } else {
                    console.error('USD rate not found in API response', res);
                    return euroAmount; // fallback: return same amount
                }
            }),
            catchError(err => {
                console.error('Error fetching exchange rate', err);
                return of(euroAmount); // fallback: return same amount
            })
        );
    }
    /** 
     * Create a new billing portal session
     * */
    createBillingPortalSession(data: any): Observable<object> {
        return this.http.post(`${environment.apiUrl}wallet/billing-portal`, data);
    }

    /** 
     * Create a new top-up session
     * */
    createTopUpSession(data: any): Observable<object> {
        return this.http.post(`${environment.apiUrl}wallet/top-up`, data);
    }

    /**
     * Make Initial Payment during Signup
     */
    makeInitialPayment(payload: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}wallet/make-initial-payment`, payload);
    }

    /**
     * Determine Price based on token
     * If exist than return pricing Group details of user belong to it
     * If not exist than return pricing Group based on user's country
     */

    getPriceDetermination(payload: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}wallet/price-determination`, payload);
    }

    /**
     * Check user signup status by email or token
     * Returns user status: active, payment_pending, or not found
     * If payment_pending, includes information about incomplete payment
     */
    checkUserSignupStatus(emailOrToken: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}wallet/check-signup-status`, {
            emailOrToken: emailOrToken,
        });
    }

    /**
     * Redirect to pending checkout session
     * */
    redirectToCheckoutSession(sessionId: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}wallet/redirect-checkout-session`, { sessionId });
    }

    /**
     * Adjust wallet balance (Admin only)
     * */
    adjustBalance(payload: {
        agencyId: string,
        amountCents: number,
        reason: string
    }): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}wallet/adjust-balance`, payload);
    }

    getTransactionalValues(agencyId: string): Observable<any> {
        return this.http.get<any>(
            `${environment.apiUrl}wallet/${encodeURIComponent(agencyId)}/transactional-values`
        );
    }

    updateProviderCreditOverride(
        agencyId: string,
        payload: { provider_name: string; credit_value?: number | null; service?: string }
    ): Observable<any> {
        return this.http.put<any>(
            `${environment.apiUrl}wallet/${encodeURIComponent(agencyId)}/provider-credit-overrides`,
            payload
        );
    }

    getAgencyContractInfo(agencyId: string): Observable<any> {
        return this.http.get<any>(
            `${environment.apiUrl}wallet/${encodeURIComponent(agencyId)}/contract-info`
        );
    }

    listPromotionalLinks(): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}wallet/promotional-links`);
    }

    createPromotionalLink(payload: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}wallet/promotional-links`, payload);
    }

    updatePromotionalLink(id: string, payload: any): Observable<any> {
        return this.http.patch<any>(
            `${environment.apiUrl}wallet/promotional-links/${encodeURIComponent(id)}`,
            payload
        );
    }

    getPromotionalLinkById(id: string): Observable<any> {
        return this.http.get<any>(
            `${environment.apiUrl}wallet/promotional-links/${encodeURIComponent(id)}`
        );
    }

    getPromotionalLinkMembershipStats(id: string, organisationKey?: number | string | null): Observable<any> {
        let url = `${environment.apiUrl}wallet/promotional-links/${encodeURIComponent(id)}/membership-stats`;
        if (organisationKey != null && organisationKey !== '') {
            url += `?organisation=${encodeURIComponent(String(organisationKey))}`;
        }
        return this.http.get<any>(url);
    }

    deletePromotionalLink(id: string): Observable<any> {
        return this.http.delete<any>(
            `${environment.apiUrl}wallet/promotional-links/${encodeURIComponent(id)}`
        );
    }

    getPublicPromoLink(secretKey: string, lang?: string): Observable<any> {
        const langQuery = lang ? `?lang=${encodeURIComponent(lang)}` : '';
        return this.http.get<any>(
            `${environment.apiUrl}wallet/promotional-links/public/${encodeURIComponent(secretKey)}${langQuery}`
        );
    }
}